import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
} from '@angular/fire/firestore';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  Observable,
  from,
  switchMap,
  map,
  combineLatest,
  of,
  startWith,
  tap,
  catchError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { Auth } from '@angular/fire/auth';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(
    private firestore: Firestore,
    private http: HttpClient,
    private auth: Auth,
    private userPreferences: UserPreferencesService,
  ) {}

  // Get a single document as an observable (like docData)
  getDocument<T>(
    collectionName: string,
    docId: string,
  ): Observable<T | undefined> {
    const docRef = doc(this.firestore, collectionName, docId);
    return docData(docRef) as Observable<T | undefined>;
  }

  // Get all documents from a collection
  getCollection<T>(
    collectionName: string,
    applyYearFilter: boolean = true,
  ): Observable<T[]> {
    // Use year-based collections for portal-data when feature flag is enabled
    // IMPORTANT: When useYearBasedCollections is true, ALWAYS use year-based collections
    // for portal-data, regardless of applyYearFilter setting. The year filter controls
    // WHICH year collections to query, not whether to use year-based collections.
    if (
      collectionName === 'portal-data' &&
      environment.useYearBasedCollections
    ) {
      // Get enabled years from user preferences and convert to Observable
      return from(this.userPreferences.getEnabledYears()).pipe(
        switchMap((enabledYears: number[]) => {
          // Convert full years to fiscal year format (2024 → "fy24")
          const fiscalYears = enabledYears.map((year: number) => {
            const fiscalYearNum = year % 100;
            return `fy${fiscalYearNum}`;
          });

          console.log('[DataService] Using year-based collections', {
            enabledYears,
            fiscalYears,
            applyYearFilter,
          });

          // Use the new year-based query method
          // This will query only the enabled year collections
          return this.getPortalDataByYears<T>(fiscalYears);
        }),
      );
    }

    const collectionRef = collection(this.firestore, collectionName);

    // For progress-data, use getDocs to fetch all data at once
    if (collectionName === 'progress-data' && !applyYearFilter) {
      const q = query(collectionRef, orderBy('date', 'asc'));
      return from(getDocs(q)).pipe(
        map((snapshot) => snapshot.docs.map((doc) => doc.data() as T)),
      );
    }

    const data$ = collectionData(collectionRef) as Observable<T[]>;

    if (!applyYearFilter) {
      return data$;
    }

    // Apply year filtering for QuickBooks collections
    // Note: portal-data is handled above via year-based collections when feature flag is enabled
    const collectionsToFilter = ['from-quickbooks', 'from-quickbooks-raw'];

    if (collectionsToFilter.includes(collectionName)) {
      return data$.pipe(
        switchMap(async (items) => {
          return await this.filterByEnabledYears(items, collectionName);
        }),
      );
    }

    return data$;
  }

  /**
   * Get portal data from year-based collections
   * @param fiscalYears - Array of fiscal years to query (e.g., ["fy24", "fy25"])
   * @returns Observable of combined project data from all specified year collections
   */
  getPortalDataByYears<T>(fiscalYears: string[]): Observable<T[]> {
    // ALWAYS include "unknown" in the query
    const yearsToQuery = [...new Set([...fiscalYears, 'unknown'])];

    // Handle empty fiscalYears array - still query unknown
    if (yearsToQuery.length === 0) {
      yearsToQuery.push('unknown');
    }

    // Map fiscal years to collection names
    const collectionNames = yearsToQuery.map((fy) => `portal-data-${fy}`);

    console.log('[DataService] Querying year-based collections', {
      fiscalYears,
      yearsToQuery,
      collectionNames,
    });

    // Create observables for each collection
    // Use startWith([]) to ensure each observable emits immediately, even if collection is empty
    // This is required for combineLatest to work - it waits for ALL observables to emit at least once
    const observables = collectionNames.map((collectionName) => {
      const collectionRef = collection(this.firestore, collectionName);
      return (collectionData(collectionRef) as Observable<T[]>).pipe(
        startWith([]),
      );
    });

    // If no observables, return empty array
    if (observables.length === 0) {
      return of([]);
    }

    // Combine all observables and flatten the results
    return combineLatest(observables).pipe(
      map((arrays) => {
        // Flatten array of arrays into single array
        const flattened = arrays.flat();
        console.log('[DataService] Combined year-based data', {
          collections: collectionNames.length,
          totalDocuments: flattened.length,
        });
        return flattened;
      }),
    );
  }

  // Get filtered documents from a collection
  getFilteredCollection<T>(
    collectionName: string,
    fieldPath: string,
    operator: any,
    value: any,
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, collectionName);
    const q = query(collectionRef, where(fieldPath, operator, value));
    return collectionData(q) as Observable<T[]>;
  }

  // Get progress data filtered by date range (optimized for performance)
  getProgressDataByDateRange<T>(
    startDate: string,
    endDate?: string,
  ): Observable<T[]> {
    const collectionRef = collection(this.firestore, 'progress-data');
    let q;

    if (endDate) {
      // Query for date range
      q = query(
        collectionRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
      );
    } else {
      // Query for dates >= startDate
      q = query(
        collectionRef,
        where('date', '>=', startDate),
        orderBy('date', 'asc'),
      );
    }

    return collectionData(q) as Observable<T[]>;
  }

  // Add a new document to a collection
  async addDocument(
    collectionName: string,
    data: any,
    id?: string,
  ): Promise<void> {
    const collectionRef = collection(this.firestore, collectionName);
    if (id) {
      await setDoc(doc(collectionRef, id), data);
    } else {
      await setDoc(doc(collectionRef), data);
    }
  }

  // Update an existing document
  async updateDocument(
    collectionName: string,
    id: string,
    data: any,
  ): Promise<void> {
    const docRef = doc(this.firestore, collectionName, id);
    await updateDoc(docRef, data);
  }

  // Delete a document
  async deleteDocument(collectionName: string, id: string): Promise<void> {
    const docRef = doc(this.firestore, collectionName, id);
    await deleteDoc(docRef);
  }

  /**
   * Fetch purchase orders from year-based collections via backend HTTP endpoint
   * Queries purchase-orders-fy24, purchase-orders-fy25, etc. based on enabled years
   */
  getPurchaseOrders(): Observable<any[]> {
    console.log('[DataService] getPurchaseOrders() called');
    // Get enabled years from user preferences and current user's ID token
    return from(this.userPreferences.getEnabledYears()).pipe(
      switchMap((enabledYears: number[]) => {
        console.log('[DataService] Enabled years retrieved:', enabledYears);
        return from(
          this.auth.currentUser?.getIdToken() || Promise.resolve(null),
        ).pipe(
          switchMap((token) => {
            console.log(
              '[DataService] Auth token retrieved:',
              token ? 'YES' : 'NO',
            );
            const headers = new HttpHeaders({
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            });

            // Pass enabled years as query parameter
            const params = new HttpParams().set(
              'enabledYears',
              JSON.stringify(enabledYears),
            );

            console.log(
              '[DataService] Fetching purchase orders for years:',
              enabledYears,
            );

            return this.http
              .get<
                any[]
              >(`${environment.functionsUrl}/purchaseOrders`, { headers, params })
              .pipe(
                tap((result) =>
                  console.log(
                    '[DataService] PO HTTP response:',
                    result?.length || 0,
                  ),
                ),
                catchError((error) => {
                  console.error('[DataService] PO HTTP error:', error);
                  return of([]);
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        console.error('[DataService] getPurchaseOrders error:', error);
        return of([]);
      }),
    );
  }

  /**
   * Filter data by enabled years based on user preferences
   * NOTE: This method is only used for legacy collections when year-based collections are disabled.
   * When environment.useYearBasedCollections is true, portal-data filtering is handled by
   * querying only the enabled year collections (portal-data-fy24, portal-data-fy25, etc.)
   */
  private async filterByEnabledYears(
    data: any[],
    collectionName?: string,
  ): Promise<any[]> {
    try {
      const enabledYears = await this.userPreferences.getEnabledYears();
      console.log(
        `[DataService] Filtering ${
          collectionName || 'collection'
        } by enabled years:`,
        enabledYears,
      );

      if (!enabledYears || enabledYears.length === 0) {
        return data;
      }

      const filtered = data.filter((item) => {
        // For QuickBooks collections, use date fields
        if (
          collectionName === 'from-quickbooks' ||
          collectionName === 'from-quickbooks-raw'
        ) {
          // from-quickbooks (expenses/line items) use 'date' field (extracted from bill.TxnDate)
          if (item.date) {
            const year = this.extractYear(item.date);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // from-quickbooks-raw (bills) use TxnDate as primary date
          if (item.TxnDate) {
            const year = this.extractYear(item.TxnDate);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // Fallback to MetaData.CreateTime
          if (item.MetaData?.CreateTime) {
            const year = this.extractYear(item.MetaData.CreateTime);
            if (year) {
              return enabledYears.includes(year);
            }
          }
          // If no date found, exclude
          return false;
        }

        // For portal-data, prioritize creation date fields
        const creationDateFields = [
          'date_created',
          'dateCreated',
          'createdDate',
        ];

        // First check creation date
        for (const field of creationDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              // If we found a creation year, use it to determine inclusion
              return enabledYears.includes(year);
            }
          }
        }

        // If no creation date found, check other date fields as fallback
        const otherDateFields = [
          'date_updated',
          'dateUpdated',
          'start_date',
          'startDate',
          'due_date',
          'dueDate',
          'date_closed',
          'dateClosed',
        ];

        for (const field of otherDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              return enabledYears.includes(year);
            }
          }
        }

        // If no date fields found at all, exclude the item
        console.warn('[DataService] Item has no date fields:', item);
        return false;
      });

      console.log(
        `[DataService] Filtered ${collectionName || 'collection'}: ${
          data.length
        } items to ${filtered.length} items`,
      );
      return filtered;
    } catch (error) {
      console.error('[DataService] Error filtering by years:', error);
      return data;
    }
  }

  /**
   * Extract year from various date formats
   */
  private extractYear(date: any): number | null {
    try {
      let dateObj: Date;

      if (date && date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        return null;
      }

      if (isNaN(dateObj.getTime())) {
        return null;
      }

      return dateObj.getFullYear();
    } catch (error) {
      return null;
    }
  }
}
